/**
 * Payee naming proposals (TMMAPP-0032) — groups, never a transaction list. Keyboard-first
 * review (↑/↓, Enter accept, E edit, F forget — R kept as an alias, TMMAPP-0136 item 4a) and the
 * high-confidence (90 %) bulk accept button.
 * The list is a `list` of `listitem`s with a roving tab stop (TMMAPP-0121 item 17; a `listbox`
 * may not hold buttons): ↑/↓ move focus between rows, the keys act on the focused row, and each
 * row is named with its position ("2 of 5: …"). A key pressed on a row's own button is the button's.
 *
 * Each suggestion is labelled (TMMAPP-0120 item 16): the bank's text after "From your bank", its
 * transaction count, "Suggested name: …", and one confidence signal (the "High confidence" chip at
 * 0.9 or more, never a bare percentage). The source follows TMM-B9 from the served `source` only,
 * through the shared pills (TMMAPP-0121 §B): an AI suggestion is `AiPill` with its confidence in
 * words (no separate chip), a `DETERMINISTIC` one is `NoAiPill` (TMMAPP-0119 item 26); `MEMORY` and
 * an absent source carry no mark. The kind never decides: AI_PAYEE_PROPOSAL carries both.
 *
 * Edit makes reusing a payee the easy path: it opens with the suggested name selected and the
 * person's existing payees listed; typing searches them, a name that matches none is a separate
 * last option ('Create "…"'), Enter takes the highlighted option (or, with none highlighted, the
 * typed text), and a near miss offers "Did you mean …?" (`payeeCloseness`).
 *
 * TMMAPP-0138 (TMMAPI-0174 §F–G, 0175): Forget suggestion's snackbar carries Undo (`…/restore`),
 * and "Forgotten suggestions" lists what was forgotten, each with Bring back. A suggestion keyed by
 * a counterparty identity (`identityKey`) also offers "This is my account" beside Accept: the
 * shared own-account flow, which claims its lines as transfers. A `404` on accept or forget means
 * tmm-api withdrew the suggestion (own-account money): the list refetches and says so.
 *
 * TMMAPP-0162: on Payees (not embedded), "Names to review" isn't drawn with no proposals: no heading,
 * no card, no Accept all (item 8); a "Forgotten suggestions" link stays reachable on its own. A
 * reload (`reloadToken`, after an AI run) keeps the rows on screen until the new list arrives, with
 * no skeleton swap, and keeps the keyboard focus on the same suggestion, else the first row (item 10).
 *
 * TMMAPP-0163 item 4: a suggestion that serves `namingSource` shows it (`NamingSourcePill`): **AI**
 * is the AI pill with its confidence in words, the free ones (**Known merchant**, **From the bank
 * text**) the neutral pill, each with its help. Without one, the `source` mark above stands. A run's
 * growing list re-reads here in place, so rows can be accepted while it runs.
 */
import { useCallback, useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Link,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  type Theme,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { Trans, useTranslation } from 'react-i18next';
import { errorCopy } from '@/api/errorCopy';
import { ErrorCodes, isApiError } from '@/api/errors';
import { payeesApi, type Payee } from '@/api/payees';
import { payeeSuggestionsApi } from '@/api/payeeSuggestions';
import { reviewApi, type ReviewItem } from '@/api/review';
import { transfersApi } from '@/api/transfers';
import { AiPill, NoAiPill } from '@/components/AiPill';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ErrorState } from '@/components/ErrorState';
import { formatCivilDate, formatMoneyMinor } from '@/i18n/format';
import { resolveTokens } from '@/theme';
import { useLocale } from '@/i18n/useLocale';
import { useMe } from '@/session/meHooks';
import type { NotifyOptions } from '@/shell/NotifyProvider';
import type { ReviewCount } from '@/components/reviewTab';
import { OwnAccountDialog } from '@/features/accounts/OwnAccountDialog';
import { prefillLast4, servedIdentifierKind } from '@/features/accounts/identifierDisplay';
import { ForgottenSuggestionsDialog } from './ForgottenSuggestionsDialog';
import { hasForgottenSuggestions } from './forgottenPages';
import { closestName, sameName, searchWord } from './payeeCloseness';
import { NamingSourcePill } from './NamingSourcePill';
import { SuggestionLinesDialog } from './SuggestionLinesDialog';

const HIGH_CONFIDENCE = 0.9;
const SEARCH_DEBOUNCE_MS = 200;

export interface PayeeProposalListProps {
  /** Bump to force a refetch (e.g. after an AI run). */
  reloadToken?: number | undefined;
  /** Called after a decision batch so the coverage card can refetch (caller debounces). */
  onDecided?: (() => void) | undefined;
  /** A notice; Forget suggestion's carries Undo (`options.action`). */
  onNotify?: ((message: string, options?: NotifyOptions) => void) | undefined;
  /** Compact variant when embedded in the review queue. */
  embedded?: boolean | undefined;
  /**
   * The host already says "All caught up" at zero (Payees' Items to review), so the list adds no
   * "Nothing left to name." line.
   */
  caughtUpShown?: boolean | undefined;
  /**
   * The number of suggestions to review, on every change: `loading` while a read is in flight,
   * `unavailable` when it failed (the Payees page's Items to review count, TMMAPP-0159 §F 31).
   */
  onCount?: ((count: ReviewCount) => void) | undefined;
}

type State =
  | { status: 'loading' }
  | { status: 'ready'; items: ReviewItem[] }
  | { status: 'error'; error: unknown };

/** The typed name as its own last option, when it matches no existing payee. */
interface CreateOption {
  create: string;
}
type EditOption = Payee | CreateOption;

function isCreate(option: EditOption): option is CreateOption {
  return 'create' in option;
}

function proposedLabel(item: ReviewItem): string {
  if (item.proposedPayee !== null) return item.proposedPayee.name;
  return item.proposedName ?? '';
}

/** A chip shows its whole label, wrapping inside itself rather than ending in "…" (item 5). */
const FULL_CHIP = {
  height: 'auto',
  maxWidth: '100%',
  '& .MuiChip-label': { whiteSpace: 'normal', overflow: 'visible' },
} as const;

/**
 * TMM-B9: the served source's mark (the shared pills); nothing when the wire is silent. How the name
 * was made (`namingSource`, TMMAPP-0163 item 4) wins when served.
 */
function SourceMark({ item }: { item: ReviewItem }) {
  if (item.namingSource !== undefined) {
    return (
      <NamingSourcePill
        source={item.namingSource}
        confidence={item.namingSource === 'AI' ? item.confidence : null}
      />
    );
  }
  if (item.source === 'AI') return <AiPill confidence={item.confidence} />;
  if (item.source === 'DETERMINISTIC') return <NoAiPill />;
  return null;
}

export function PayeeProposalList({
  reloadToken = 0,
  onDecided,
  onNotify,
  embedded = false,
  caughtUpShown = false,
  onCount,
}: PayeeProposalListProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const me = useMe();
  const currency = me.user.currency;
  const [state, setState] = useState<State>({ status: 'loading' });
  const [focusIndex, setFocusIndex] = useState(0);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editText, setEditText] = useState('');
  // Until the person types, the list is their existing payees (the search with no `q`).
  const [typed, setTyped] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [payeeOptions, setPayeeOptions] = useState<Payee[]>([]);
  const [candidates, setCandidates] = useState<Payee[]>([]);
  const [acceptAllOpen, setAcceptAllOpen] = useState(false);
  const [acceptAllError, setAcceptAllError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const listRef = useRef<HTMLDivElement | null>(null);
  const rowRefs = useRef<(HTMLDivElement | null)[]>([]);
  // A keyboard Accept / Forget removes the focused row: focus the row that takes its place.
  const refocusRow = useRef(false);
  const editInputRef = useRef<HTMLInputElement | null>(null);
  const forgetHintId = useId();
  // "Forgotten suggestions" shows once the first page has one (TMMAPP-0138 item 6).
  const [hasForgotten, setHasForgotten] = useState(false);
  const [forgottenOpen, setForgottenOpen] = useState(false);
  // The suggestion "This is my account" is claiming (TMMAPP-0138 item 7).
  const [ownFor, setOwnFor] = useState<{ identityKey: string; item: ReviewItem } | null>(null);
  // An account number, IBAN or CLABE the item serves (an AI proposal usually serves none).
  const ownIdentifier =
    ownFor !== null &&
    servedIdentifierKind(ownFor.item.identifierKind) !== null &&
    ownFor.item.identifier !== null
      ? ownFor.item.identifier
      : '';
  // The suggestion whose transactions are open (TMMAPP-0136 item 9).
  const [linesOf, setLinesOf] = useState<{
    suggestionId: number;
    bankText: string;
    lineCount: number | null;
  } | null>(null);

  // What the last render showed, for a reload in place (TMMAPP-0162 item 10).
  const shown = useRef<{ items: ReviewItem[]; focus: number }>({ items: [], focus: 0 });
  const [loadTick, setLoadTick] = useState(0);

  const load = useCallback(async () => {
    // The skeleton is the first load's only: a reload keeps the rows until the new list arrives.
    setState((prev) => (prev.status === 'ready' ? prev : { status: 'loading' }));
    // The page carries no total, so the link never counts (item 6): it shows when there is one,
    // reading on past empty pages that carry a cursor (TMMAPP-0139 item 9).
    void hasForgottenSuggestions()
      .then(setHasForgotten)
      .catch(() => setHasForgotten(false));
    try {
      const items = await reviewApi.list('AI_PAYEE_PROPOSAL');
      const before = shown.current;
      const focusedId = before.items[before.focus]?.suggestionId ?? null;
      const kept =
        focusedId === null ? -1 : items.findIndex((row) => row.suggestionId === focusedId);
      // A row that had the keyboard focus keeps it on the same suggestion, else the first row.
      if (listRef.current?.contains(document.activeElement) === true) refocusRow.current = true;
      setState({ status: 'ready', items });
      setFocusIndex(kept >= 0 ? kept : 0);
      setEditingId((prev) =>
        prev !== null && items.some((row) => row.suggestionId === prev) ? prev : null,
      );
      setLoadTick((n) => n + 1);
    } catch (error) {
      // A failed reload keeps the rows it had; only a first load shows the section error.
      setState((prev) => (prev.status === 'ready' ? prev : { status: 'error', error }));
    }
  }, []);

  useEffect(() => {
    queueMicrotask(() => {
      void load();
    });
  }, [load, reloadToken]);

  const query = typed ? editText.trim() : '';
  useEffect(() => {
    if (editingId === null) return;
    let cancelled = false;
    const handle = window.setTimeout(() => {
      void payeesApi
        .list({ status: 'ACTIVE', ...(query.length > 0 ? { q: query } : {}) })
        .then((rows) => {
          if (!cancelled) setPayeeOptions(rows);
        })
        .catch(() => {
          if (!cancelled) setPayeeOptions([]);
        });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [editingId, query]);

  // "Did you mean": the payees search on the typed name's first word, compared here.
  const word = typed ? searchWord(editText) : null;
  useEffect(() => {
    if (editingId === null || word === null) return;
    let cancelled = false;
    const handle = window.setTimeout(() => {
      void payeesApi
        .list({ status: 'ACTIVE', q: word })
        .then((rows) => {
          if (!cancelled) setCandidates(rows);
        })
        .catch(() => {
          if (!cancelled) setCandidates([]);
        });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [editingId, word]);

  // Opening Edit: focus the field with the suggested name selected, so typing replaces it.
  useEffect(() => {
    if (editingId === null) return;
    const input = editInputRef.current;
    input?.focus();
    input?.select();
  }, [editingId]);

  const items = state.status === 'ready' ? state.items : [];
  const readyCount = state.status === 'ready' ? state.items.length : null;
  useEffect(() => {
    if (!refocusRow.current || readyCount === null) return;
    refocusRow.current = false;
    rowRefs.current[Math.min(focusIndex, readyCount - 1)]?.focus();
  }, [readyCount, focusIndex, loadTick]);
  useEffect(() => {
    onCount?.(readyCount ?? (state.status === 'error' ? 'unavailable' : 'loading'));
  }, [readyCount, state.status, onCount]);
  const highCount = items.filter(
    (row) => row.confidence !== null && row.confidence >= HIGH_CONFIDENCE,
  ).length;
  const activeFocus = items.length === 0 ? 0 : Math.min(focusIndex, items.length - 1);
  if (activeFocus !== focusIndex) {
    setFocusIndex(activeFocus);
  }
  useEffect(() => {
    shown.current = { items, focus: activeFocus };
  });

  const typedName = editText.trim();
  const known = [...payeeOptions, ...candidates];
  const matchesExisting = known.some((p) => sameName(p.name, typedName));
  const editOptions: EditOption[] =
    typed && typedName.length > 0 && !matchesExisting
      ? [...payeeOptions, { create: typedName }]
      : payeeOptions;
  const didYouMean = typed && word !== null ? closestName(typedName, candidates) : null;

  function removeLocal(suggestionId: number) {
    setState((prev) => {
      if (prev.status !== 'ready') return prev;
      const next = prev.items.filter((row) => row.suggestionId !== suggestionId);
      return { status: 'ready', items: next };
    });
    onDecided?.();
  }

  // `refocus`: a keyboard decision on the focused row. Focus moves to the next row only once the
  // row has actually gone (TMMAPP-0122 item 11), never after a refused or skipped one.
  async function handleAccept(
    item: ReviewItem,
    body?: { payeeId: number } | { name: string },
    refocus = false,
  ) {
    if (item.suggestionId === null || busy) return;
    setBusy(true);
    try {
      await payeeSuggestionsApi.accept(item.suggestionId, body ?? {});
      setEditingId(null);
      if (refocus) refocusRow.current = true;
      removeLocal(item.suggestionId);
    } catch (err) {
      if (!withdrawn(err)) onNotify?.(errorCopy(t, err, 'accounts.errorGeneric'));
    } finally {
      setBusy(false);
    }
  }

  /**
   * A `404` on accept or forget (TMMAPI-0174 §F): tmm-api withdrew the suggestion (own-account
   * money is never a payee). The list refetches and says so; anything else is the caller's.
   */
  function withdrawn(err: unknown): boolean {
    if (!isApiError(err) || err.status !== 404) return false;
    onNotify?.(t('naming.noLongerNeeded'));
    void load();
    onDecided?.();
    return true;
  }

  /**
   * Bring a forgotten suggestion back (`…/restore`, TMMAPI-0174 §G) and refetch: it is back in its
   * place in the list and the counts. A `409` (accepted, or never a payee) says so; a `404`
   * refetches silently. True when it left the forgotten list.
   */
  async function restore(suggestionId: number): Promise<boolean> {
    try {
      await payeeSuggestionsApi.restore(suggestionId);
      void load();
      onDecided?.();
      return true;
    } catch (err) {
      if (isApiError(err) && err.code === ErrorCodes.PAYEE_SUGGESTION_DECIDED) {
        onNotify?.(t('naming.cantBringBack'));
        void load();
        return false;
      }
      if (isApiError(err) && err.status === 404) {
        void load();
        return true;
      }
      onNotify?.(errorCopy(t, err, 'accounts.errorGeneric'));
      return false;
    }
  }

  // Forget suggestion (TMMAPP-0136 item 4a): `…/reject`, so the group is never suggested again.
  // Undo restores it (TMMAPP-0138 item 6).
  async function handleForget(item: ReviewItem, refocus = false) {
    const suggestionId = item.suggestionId;
    if (suggestionId === null || busy) return;
    setBusy(true);
    try {
      await payeeSuggestionsApi.reject(suggestionId);
      if (refocus) refocusRow.current = true;
      removeLocal(suggestionId);
      setHasForgotten(true);
      onNotify?.(t('naming.forgotten'), {
        action: { label: t('naming.undo'), onClick: () => void restore(suggestionId) },
      });
    } catch (err) {
      if (!withdrawn(err)) onNotify?.(errorCopy(t, err, 'accounts.errorGeneric'));
    } finally {
      setBusy(false);
    }
  }

  function startEdit(item: ReviewItem) {
    if (item.suggestionId === null) return;
    setEditingId(item.suggestionId);
    setEditText(proposedLabel(item));
    setTyped(false);
    setPayeeOptions([]);
    setCandidates([]);
    setEditOpen(true);
  }

  function cancelEdit() {
    setEditingId(null);
    setEditOpen(false);
  }

  /** Accept with the typed text: tmm-api reuses a payee of that exact name, else creates it. */
  async function commitTyped(item: ReviewItem) {
    const name = editText.trim();
    if (name.length === 0) return;
    await handleAccept(item, { name });
  }

  function commitOption(item: ReviewItem, option: EditOption | string) {
    if (typeof option === 'string') {
      const name = option.trim();
      if (name.length > 0) void handleAccept(item, { name });
      return;
    }
    if (isCreate(option)) {
      void handleAccept(item, { name: option.create });
      return;
    }
    void handleAccept(item, { payeeId: option.id });
  }

  function openAcceptAll() {
    setAcceptAllError(null);
    setAcceptAllOpen(true);
  }

  /** The bulk accept: the confirmation stays open and shows a failure inline (TMM-B3). */
  async function handleAcceptAll() {
    if (busy) return;
    setAcceptAllError(null);
    setBusy(true);
    try {
      const result = await payeeSuggestionsApi.acceptAll(HIGH_CONFIDENCE);
      setAcceptAllOpen(false);
      onNotify?.(t('naming.acceptedAll', { accepted: result.accepted, lines: result.lines }));
      await load();
      onDecided?.();
    } catch (err) {
      setAcceptAllError(errorCopy(t, err, 'accounts.errorGeneric'));
    } finally {
      setBusy(false);
    }
  }

  function handleListKeyDown(event: KeyboardEvent) {
    if (state.status !== 'ready' || items.length === 0) return;
    if (editingId !== null) {
      if (event.key === 'Escape') {
        event.preventDefault();
        cancelEdit();
      }
      return;
    }
    // Keys on a row's own controls (Accept, Edit, Forget) belong to those controls.
    if (
      !(event.target instanceof HTMLElement) ||
      event.target.getAttribute('role') !== 'listitem'
    ) {
      return;
    }
    const item = items[activeFocus];
    if (item === undefined) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const next =
        event.key === 'ArrowDown'
          ? Math.min(items.length - 1, activeFocus + 1)
          : Math.max(0, activeFocus - 1);
      setFocusIndex(next);
      rowRefs.current[next]?.focus();
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      void handleAccept(item, undefined, true);
      return;
    }
    if (event.key === 'e' || event.key === 'E') {
      event.preventDefault();
      startEdit(item);
      return;
    }
    // F forgets; R, the old Reject key, stays an alias.
    if (event.key === 'f' || event.key === 'F' || event.key === 'r' || event.key === 'R') {
      event.preventDefault();
      void handleForget(item, true);
    }
  }

  if (state.status === 'loading') {
    return <Skeleton variant="rounded" height={120} role="status" aria-label={t('naming.title')} />;
  }

  if (state.status === 'error') {
    return (
      <ErrorState
        variant="section"
        error={state.error}
        what={t('errors.what.payeeProposals')}
        onRetry={() => void load()}
      />
    );
  }

  const forgottenLink = hasForgotten && (
    <Link
      component="button"
      type="button"
      variant="body2"
      data-forgotten-link=""
      onClick={() => setForgottenOpen(true)}
      sx={{ alignSelf: 'flex-start' }}
    >
      {t('naming.forgottenLink')}
    </Link>
  );
  const forgottenDialog = forgottenOpen && (
    <ForgottenSuggestionsDialog onRestore={restore} onClose={() => setForgottenOpen(false)} />
  );

  // Payees draws no empty "Names to review" (TMMAPP-0162 item 8): the tab shows the coverage card,
  // or its own "All caught up". Forgotten suggestions stay a link away.
  if (!embedded && items.length === 0) {
    return (
      <>
        {forgottenLink}
        {forgottenDialog}
      </>
    );
  }

  const Wrapper = embedded ? Box : Paper;

  return (
    <Wrapper
      {...(embedded
        ? { sx: { mt: 1 } }
        : { variant: 'outlined' as const, sx: { p: 2 }, component: undefined })}
    >
      <Stack spacing={1.5}>
        {!embedded && (
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            sx={{ gap: 1, alignItems: { sm: 'center' }, justifyContent: 'space-between' }}
          >
            <Typography variant="h2">{t('naming.reviewTitle')}</Typography>
            {highCount > 0 && (
              <Button variant="outlined" onClick={openAcceptAll} disabled={busy}>
                {t('naming.acceptAll', { count: highCount })}
              </Button>
            )}
          </Stack>
        )}
        {embedded && highCount > 0 && (
          <Button
            variant="outlined"
            size="small"
            onClick={openAcceptAll}
            disabled={busy}
            sx={{ alignSelf: 'flex-start' }}
          >
            {t('naming.acceptAll', { count: highCount })}
          </Button>
        )}

        {items.length === 0 ? (
          !caughtUpShown && (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {t('naming.allDone')}
            </Typography>
          )
        ) : (
          <>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {t('naming.keysHint')}
            </Typography>
            <Box component="span" id={forgetHintId} sx={visuallyHidden}>
              {t('naming.forgetHint')}
            </Box>
            <Stack
              ref={listRef}
              role="list"
              aria-label={t('naming.reviewTitle')}
              onKeyDown={handleListKeyDown}
              spacing={1}
            >
              {items.map((item, index) => {
                const id = item.suggestionId ?? index;
                const focused = index === activeFocus;
                const high = item.confidence !== null && item.confidence >= HIGH_CONFIDENCE;
                const sample = item.sampleLine;
                const suggestionId = item.suggestionId;
                const editing = editingId !== null && editingId === item.suggestionId;
                return (
                  <Box
                    key={id}
                    ref={(node: HTMLDivElement | null) => {
                      rowRefs.current[index] = node;
                    }}
                    role="listitem"
                    tabIndex={focused ? 0 : -1}
                    aria-label={t('naming.rowPosition', {
                      position: index + 1,
                      count: items.length,
                      bank: item.payeeNorm ?? '',
                      name: proposedLabel(item),
                    })}
                    aria-posinset={index + 1}
                    aria-setsize={items.length}
                    onClick={() => setFocusIndex(index)}
                    onFocus={() => setFocusIndex(index)}
                    sx={{
                      outline: 'none',
                      p: 1.5,
                      borderRadius: 1,
                      border: '1px solid',
                      // The focused option stays on the canvas: its captions (`text2`), chips and
                      // field borders are guaranteed there, not under `selected` (TMMAPP-0049). A
                      // 2px `brandBoundary` ring (border + inset shadow, no layout shift) marks it.
                      borderColor: (theme: Theme) =>
                        focused ? resolveTokens(theme).brandBoundary : theme.palette.divider,
                      boxShadow: (theme: Theme) =>
                        focused ? `inset 0 0 0 1px ${resolveTokens(theme).brandBoundary}` : 'none',
                    }}
                  >
                    {/* The text takes the full width; the actions are one row below it, wrapping
                        on a narrow card (TMMAPP-0139 item 5). */}
                    <Stack spacing={1.5}>
                      <Stack spacing={0.5} sx={{ minWidth: 0 }}>
                        <Stack
                          direction="row"
                          sx={{ gap: 1, alignItems: 'center', flexWrap: 'wrap' }}
                        >
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {t('naming.fromYourBank')}
                          </Typography>
                          <Typography
                            variant="mono"
                            component="span"
                            sx={{ overflowWrap: 'anywhere' }}
                          >
                            {item.payeeNorm ?? ''}
                          </Typography>
                          {item.lineCount !== null &&
                            (suggestionId !== null ? (
                              <Link
                                component="button"
                                type="button"
                                variant="body2"
                                data-suggestion-lines-link=""
                                onClick={() =>
                                  setLinesOf({
                                    suggestionId,
                                    bankText: item.payeeNorm ?? '',
                                    lineCount: item.lineCount,
                                  })
                                }
                                sx={{ verticalAlign: 'baseline' }}
                              >
                                {t('naming.lines', { count: item.lineCount })}
                              </Link>
                            ) : (
                              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                {t('naming.lines', { count: item.lineCount })}
                              </Typography>
                            ))}
                        </Stack>
                        {sample !== null && (
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {[
                              sample.date !== null ? formatCivilDate(sample.date, locale) : null,
                              sample.amountMinor !== null
                                ? formatMoneyMinor(sample.amountMinor, currency, locale)
                                : null,
                              sample.accountName,
                            ]
                              .filter(
                                (part): part is string =>
                                  typeof part === 'string' && part.length > 0,
                              )
                              .join(' · ')}
                          </Typography>
                        )}
                        {editing ? (
                          <Stack spacing={0.5}>
                            <Autocomplete<EditOption, false, false, true>
                              options={editOptions}
                              open={editOpen}
                              onOpen={() => setEditOpen(true)}
                              onClose={() => setEditOpen(false)}
                              openOnFocus
                              // The server already searched; never re-filter by the typed text.
                              filterOptions={(options) => options}
                              getOptionLabel={(opt) =>
                                typeof opt === 'string'
                                  ? opt
                                  : isCreate(opt)
                                    ? opt.create
                                    : opt.name
                              }
                              getOptionKey={(opt) =>
                                typeof opt === 'string'
                                  ? `text:${opt}`
                                  : isCreate(opt)
                                    ? 'create'
                                    : `payee:${opt.id}`
                              }
                              renderOption={(props, opt) => {
                                const { key, ...rest } = props;
                                return (
                                  <li key={key} {...rest}>
                                    {isCreate(opt)
                                      ? t('naming.createPayee', { name: opt.create })
                                      : opt.name}
                                  </li>
                                );
                              }}
                              value={null}
                              inputValue={editText}
                              onInputChange={(_event, value, reason) => {
                                if (reason === 'reset') return;
                                setEditText(value);
                                setTyped(true);
                                setEditOpen(true);
                              }}
                              onChange={(_event, value) => {
                                if (value !== null) commitOption(item, value);
                              }}
                              freeSolo
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  size="small"
                                  label={t('naming.payeeField')}
                                  inputRef={editInputRef}
                                  onKeyDown={(event) => {
                                    if (event.key === 'Escape') {
                                      event.preventDefault();
                                      event.stopPropagation();
                                      cancelEdit();
                                    }
                                  }}
                                />
                              )}
                            />
                            {didYouMean !== null && (
                              <Typography variant="body2" data-did-you-mean="">
                                <Trans
                                  i18nKey="naming.didYouMean"
                                  values={{ name: didYouMean.name }}
                                  components={{
                                    pick: (
                                      <Link
                                        component="button"
                                        type="button"
                                        variant="body2"
                                        disabled={busy}
                                        onClick={() =>
                                          void handleAccept(item, { payeeId: didYouMean.id })
                                        }
                                        sx={{ verticalAlign: 'baseline' }}
                                      />
                                    ),
                                  }}
                                />
                              </Typography>
                            )}
                          </Stack>
                        ) : (
                          <Stack
                            direction="row"
                            sx={{ gap: 1, alignItems: 'center', flexWrap: 'wrap' }}
                          >
                            <Typography>
                              <Trans
                                i18nKey="naming.suggestedName"
                                values={{ name: proposedLabel(item) }}
                                components={{
                                  b: <Box component="strong" sx={{ fontWeight: 500 }} />,
                                }}
                              />
                            </Typography>
                            {item.proposedPayee !== null && (
                              <Chip size="small" label={t('naming.matches')} sx={FULL_CHIP} />
                            )}
                            {high && item.source !== 'AI' && item.namingSource !== 'AI' && (
                              <Chip
                                size="small"
                                color="success"
                                label={t('naming.high')}
                                sx={FULL_CHIP}
                              />
                            )}
                            <SourceMark item={item} />
                          </Stack>
                        )}
                      </Stack>
                      <Stack
                        direction="row"
                        data-suggestion-actions=""
                        sx={{ gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-start' }}
                      >
                        {editing ? (
                          <>
                            <Button
                              size="small"
                              variant="contained"
                              disabled={busy || editText.trim().length === 0}
                              onClick={() => void commitTyped(item)}
                            >
                              {t('naming.accept')}
                            </Button>
                            <Button size="small" onClick={cancelEdit}>
                              {t('naming.cancel')}
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button
                              size="small"
                              variant="contained"
                              disabled={busy}
                              onClick={() => void handleAccept(item)}
                            >
                              {t('naming.accept')}
                            </Button>
                            {item.identityKey !== null && (
                              <Button
                                size="small"
                                disabled={busy}
                                data-own-account-action=""
                                onClick={() => {
                                  if (item.identityKey !== null) {
                                    setOwnFor({ identityKey: item.identityKey, item });
                                  }
                                }}
                              >
                                {t('wire.thisIsMine')}
                              </Button>
                            )}
                            <Button size="small" disabled={busy} onClick={() => startEdit(item)}>
                              {t('naming.edit')}
                            </Button>
                            {/* The span keeps the tooltip on a disabled button; the button's
                                own description is the hint below the list's key line. */}
                            <Tooltip title={t('naming.forgetHint')} describeChild>
                              <Box component="span" sx={{ display: 'inline-flex' }}>
                                <Button
                                  size="small"
                                  disabled={busy}
                                  aria-describedby={forgetHintId}
                                  onClick={() => void handleForget(item)}
                                >
                                  {t('naming.forget')}
                                </Button>
                              </Box>
                            </Tooltip>
                          </>
                        )}
                      </Stack>
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          </>
        )}
        {forgottenLink}
      </Stack>

      {forgottenDialog}
      {ownFor !== null && (
        <OwnAccountDialog
          title={t('wire.thisIsMine')}
          start={{ kind: 'choose' }}
          source={null}
          operatingCurrency={currency}
          // The same prefill as every entry, from what the item serves (TMMAPP-0139 item 7).
          form={{
            initialLast4: prefillLast4(ownFor.item.mask, ownIdentifier),
            initialIdentifier: ownIdentifier,
            initialInstitution: ownFor.item.counterpartyBank ?? '',
          }}
          claim={(account) => transfersApi.claimIdentity(account.id, ownFor.identityKey, true)}
          onNotify={(message) => onNotify?.(message)}
          onClose={() => setOwnFor(null)}
          onClaimed={async (result) => {
            setOwnFor(null);
            onNotify?.(t('review.missingAccount.transfersNow', { count: result.classified }));
            await load();
            onDecided?.();
          }}
        />
      )}
      {linesOf !== null && (
        <SuggestionLinesDialog
          suggestionId={linesOf.suggestionId}
          bankText={linesOf.bankText}
          lineCount={linesOf.lineCount}
          onClose={() => setLinesOf(null)}
        />
      )}
      <ConfirmDialog
        open={acceptAllOpen}
        title={t('naming.acceptAllTitle', { count: highCount })}
        consequence={t('naming.acceptAllNote')}
        confirmLabel={t('naming.acceptAllConfirm')}
        busy={busy}
        error={acceptAllError}
        onConfirm={() => void handleAcceptAll()}
        onCancel={() => setAcceptAllOpen(false)}
      />
    </Wrapper>
  );
}
